import clsx from 'clsx'
import { type ComponentProps, type ReactNode, useEffect, useRef } from 'react'
import { inputRootStyles } from './input-root.css'
import { type InputVariants, textareaStyles as styles } from './textarea.css'

interface Props
  extends Omit<ComponentProps<'textarea'>, 'prefix'>,
    InputVariants {
  error?: ReactNode | boolean
  fullWidth?: boolean
  autoGrow?: boolean
}

export function Textarea({
  variant,
  error,
  fullWidth,
  autoGrow,
  className,
  style,
  ref: propRef,
  ...props
}: Props) {
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    if (!autoGrow) return

    const textarea = textareaRef.current
    if (!textarea) return

    function onInput() {
      if (!textarea || !autoGrow) return

      if (!textarea.value) {
        textarea.style.height = 'auto'
        return
      }

      textarea.style.height = '0' // This is needed to reset the height in case of text deletion
      textarea.style.height = `${textarea.scrollHeight}px`
    }

    onInput()

    textarea.addEventListener('input', onInput, false)

    return () => {
      textarea.removeEventListener('input', onInput, false)
    }
  }, [autoGrow])

  useEffect(() => {
    // This is to handle the case where the textarea is a controlled component & the value is set to an empty string by the parent
    if (!props.value && autoGrow) {
      const textarea = textareaRef.current
      if (!textarea) return
      textarea.style.height = 'auto'
    }
  }, [props.value, autoGrow])

  const hasErrorMessage =
    typeof error !== 'boolean' && typeof error !== 'undefined' && error !== ''

  return (
    <>
      <textarea
        {...props}
        ref={textarea => {
          if (textarea) {
            if (typeof propRef === 'function') {
              propRef(textarea)
            } else if (propRef) {
              propRef.current = textarea
            }
            textareaRef.current = textarea
          }
        }}
        className={clsx(styles.textarea({ variant }), className)}
        style={style}
        data-full-width={Boolean(fullWidth)}
        data-valid={!error}
        data-autogrow={Boolean(autoGrow)}
      />

      {hasErrorMessage && (
        <span className={inputRootStyles.errorMessage}>{error}</span>
      )}
    </>
  )
}
